import { useIntl } from 'react-intl';
import { CountryCodeType } from 'models/countries';
import { useEffect, useState } from 'react';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { TreeItemWithIcon } from 'components/treeselect/CustomTreeItem';
import { TreeSelect } from 'components/treeselect/TreeSelect';
import { cn } from 'utils/classNamesTailwind';
import { Flag } from 'components/Flag';
import { TbIcon } from 'components/Figma';

interface CountrySelectProps {
  selectedCountries?: CountryCodeType[];
  setSelectedCountries?: (value: CountryCodeType[]) => void;
  className?: string;
  showConfirmButton?: boolean;
  disabled?: boolean;
}

export function CountrySelect({
  selectedCountries = undefined,
  setSelectedCountries = undefined,
  className = undefined,
  showConfirmButton = false,
  disabled = false,
}: CountrySelectProps) {
  const intl = useIntl();
  const { disabledFilters } = useTransitiveCacheStore();
  const isGlobalRegion = (countryCode: string) => countryCode.toLowerCase() === 'zz'; // global channel country code

  const { allCountries } = useTransitiveCacheStore();
  const { countries, setCountries } = useAppStore();

  const [countryTree, setCountryTree] = useState<TreeViewBaseItem<TreeItemWithIcon>[]>([]);

  const [selectedCountryAndRegions, setSelectedCountryAndRegions] = useState<string[]>([]);

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    const regionRecord: Record<string, TreeViewBaseItem<TreeItemWithIcon>> = {};
    const regionIds: string[] = [];

    allCountries.forEach(country => {
      const label = isGlobalRegion(country.countryCode)
        ? intl.formatMessage({ id: 'filters.global', defaultMessage: 'Global' })
        : (intl.formatDisplayName(country.countryCode.toUpperCase() ?? 'DE', {
            type: 'region',
          }) ??
          country.countryCode.toUpperCase() ??
          'DE');
      const countryTreeItem = {
        id: country.countryCode,
        label: label,
        icon: isGlobalRegion(country.countryCode) ? (
          <TbIcon name="Globe" />
        ) : (
          <Flag countryCode={country.countryCode} title={label} />
        ),
      };

      if (regionRecord[country.region]) {
        regionRecord[country.region].children!.push(countryTreeItem);
      } else {
        regionRecord[country.region] = {
          id: `_${country.region}`,
          label: intl.formatMessage({
            id: `attribute.chn-region.${country.region}`,
            defaultMessage: country.region,
          }),
          children: [countryTreeItem],
        };
        regionIds.push(`_${country.region}`);
      }
    });

    Object.keys(regionRecord).forEach(regionId => {
      if (regionRecord[regionId].children?.length === 1) {
        regionRecord[regionId] = regionRecord[regionId].children[0];
      }
    });

    setExpandedItems(regionIds);
    setCountryTree(Object.values(regionRecord));
  }, [allCountries, intl]);

  useEffect(() => {
    const countriesOnly = selectedCountries ?? countries ?? [];
    if (countryTree && countryTree.length > 0 && countriesOnly) {
      const fullSelection: string[] = [];
      const regionsTested: string[] = [];

      countriesOnly.forEach(countryCode => {
        const country = allCountries.find(c => c.countryCode === countryCode);
        if (country) {
          fullSelection.push(countryCode);
          const region = `_${country.region}`;
          const treeItem = countryTree.find(tbi => tbi.id === region);
          if (treeItem && !regionsTested.includes(treeItem.id)) {
            regionsTested.push(treeItem.id);
            const allRegionCountries = (treeItem.children ?? []).map(c => c.id);
            if (allRegionCountries.every(v => countriesOnly.includes(v as CountryCodeType))) {
              fullSelection.push(region);
            }
          }
        }
      });
      setSelectedCountryAndRegions(fullSelection);
    }
  }, [countries, selectedCountries, countryTree]);

  const regionAwareSetItems = (itemIds: string[]) => {
    (setSelectedCountries ?? setCountries)(itemIds.filter(id => !id.startsWith('_')) as CountryCodeType[]);
    setSelectedCountryAndRegions(itemIds);
  };

  const isDisabled = disabled || disabledFilters.includes('chnCountry');

  return (
    <TreeSelect
      disabled={isDisabled}
      defaultExpandedItems={expandedItems}
      id="country-filter"
      className={cn(isDisabled ? 'disabled' : '', className)}
      label={intl.formatMessage({
        id: 'filter.key.selectedCountries',
      })}
      items={countryTree}
      selectedItems={selectedCountryAndRegions}
      setSelectedItems={regionAwareSetItems}
      showConfirmButton={showConfirmButton}
    />
  );
}
